type QueryValue = string | number | boolean | undefined;

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  params?: Record<string, QueryValue>;
  token?: string;
};

export class ApiRequestError extends Error {
  code?: string;
  status: number;
  payload: any;

  constructor(message: string, status: number, payload: any) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.payload = payload;
    this.code = payload?.code || payload?.error;
  }
}

const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

const buildUrl = (path: string, params?: Record<string, QueryValue>) => {
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  const url = new URL(normalizedPath, normalizedBaseUrl);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
};

export const apiRequest = async <T>(path: string, options: RequestOptions = {}) => {
  const { method = "GET", body, params, token } = options;
  const activeToken = token || localStorage.getItem("parent_token") || undefined;
  const response = await fetch(buildUrl(path, params), {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(activeToken ? { Authorization: `Bearer ${activeToken}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const message = payload?.message || payload?.error || "request_failed";
    throw new ApiRequestError(message, response.status, payload);
  }

  const payload = (await response.json()) as { data?: T };
  return payload.data as T;
};
